<template>
  <main class="view details" v-if="details">
    <template v-if="['object', 'news', 'exhibits'].includes(type)">
      <Gallery :media="medias"/>
      <div class="headline">
        <div>
          <h3>{{ title }}</h3>
          <span v-if="year" class="subline">{{ year }}</span>
          <div class="teaser">
            <p v-html="short" />
            <a v-if="isAvailable" class="cta" :href="computedInterestLink" target="blank">
              <SVGChevronDownLeft />
              <span>Interesse?</span>
              <SVGChevronUpRight />
            </a>
          </div>
        </div>
      </div>
    </template>

    <template v-for="(block, idx) in blocks">
      <BlockParagraph
        v-if="block.type === 'Paragraph'"
        :key="`block-Paragraph-${idx}`"
        :content="block.data"
        :odd="idx % 2 !== 0"
      />
      <BlockParagraphPic
        v-else-if="block.type === 'ParagraphPic'"
        :key="`block-ParagraphPic-${idx}`"
        :content="block.data"
        :odd="idx % 2 !== 0"
      />
      <BlockTimeline
        v-else-if="block.type === 'Timeline'"
        :key="`block-Timeline-${idx}`"
        :content="block.data"
      />
      <BlockHero
        v-else-if="block.type === 'Hero'"
        :key="`block-Hero-${idx}`"
        :content="block.data"
      />
      <Gallery
        class="blocked"
        v-else-if="block.type === 'Gallery'"
        :key="`block-Gallery-${idx}`"
        :block-content="block.data"
      />
      <Youtube
        v-if="block.type === 'Youtube'"
        :key="`block-Youtube-${idx}`"
        :content="block.data"
      />
    </template>
  </main>
</template>

<script>
  import Gallery from '@/components/ui/Gallery';
  import CTA from '@/components/ui/CTA';
  import BlockParagraph from '@/components/ui/BlockParagraph';
  import BlockParagraphPic from '@/components/ui/BlockParagraphPic';
  import BlockTimeline from '@/components/ui/BlockTimeline';
  import BlockHero from '@/components/ui/BlockHero';
  import Youtube from '@/components/ui/Youtube';

  import SVGChevronDownLeft from '@/assets/svg/icon-chevron-down-left.svg?inline';
  import SVGChevronUpRight from '@/assets/svg/icon-chevron-up-right.svg?inline';

  export default {
    name: 'Details',
    components: {
      BlockHero,
      BlockTimeline,
      BlockParagraphPic,
      BlockParagraph,
      CTA,
      Gallery,
      Youtube,
      SVGChevronDownLeft,
      SVGChevronUpRight,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      slug: {
        type: String,
        default: null,
      },
      type: {
        type: String,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        details: null,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      title() {
        return this.details?.title || null;
      },
      short() {
        return this.details?.short || null;
      },
      isAvailable() {
        return this.details?.available || false;
      },
      blocks() {
        let blocks = this.details?.blocks;
        if (blocks) {
          blocks = blocks.constructor === String ? JSON.parse(blocks) : blocks;
          return blocks;
        }
        return [];
      },
      year() {
        return this.details?.year || null;
      },
      computedInterestLink() {
        const email = this.$t('contact_email');
        const title = this.title;
        const url = window.location.href;
        return `mailto:${email}?subject=Interesse an Werk: ${title}&body=Ich bin an diesem Werk interessiert.%0D%0ANehmen Sie bitte Kontakt mit mir auf%0D%0A%0D%0ALink zum Werk:${url}`;
      },
      medias() {
        const out = [];
        const medias = this.details?.medias || [];
        const featured = this.details?.featured;
        if (featured) {
          out.push({
            type: featured[0].type.split('/')[0],
            path: `${this.$b.contentUrl}/${featured[0]?.full || featured[0]?.path}`,
          });
        }
        medias.forEach((m) => {
          console.log(m);
          out.push({
            type: m.type.split('/')[0],
            path: `${this.$b.contentUrl}/${m.full || m.path}`,
          });
        });
        return out;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {},
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    filters: {
    },
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    async created() {
      const type = this.type;
      let apiBase = null;
      if (type === 'object') {
        apiBase = 'objects/entries';
      } else if (type === 'news') {
        apiBase = 'news/entries';
      } else if (type === 'exhibits') {
        apiBase = 'exhibits/entries';
      }

      const slug = this.slug;
      if (slug) {
        // noinspection JSVoidFunctionReturnValueUsed
        const { entries } = await this.$b.api.get(apiBase, { where: `slug=${slug}` });
        if (entries && entries.length > 0) {
          this.details = entries[0];
          this.loading = false;
        }
      }
    },
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      //----------------------------------
      // Event Handlers
      //----------------------------------
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.view.details {
  @include max-width-constraints;
  padding-top: 40px;
  padding-bottom: 100px;
  width: 100%;

  @include screen-tablet {
    padding-top: 40px;
  }

  @include screen-mobile {
    padding-top: 10px;
  }

  & > .headline {
    pointer-events: none;
    display: flex;
    justify-content: center;

    & > div {
      pointer-events: all;
      position: relative;
      margin-top: 40px;
      margin-bottom: 40px;
      width: 50%;
      min-height: 125px;
      padding: 20px 10px;
      text-align: left;

      @include screen-tablet {
        width: 550px;
        padding: 20px 0;
      }

      @include screen-mobile {
        width: 100%;
        padding: 20px 0;
      }

      & > h3 {
        font-size: 3.4rem;
      }

      & > span.subline {
        @include font-regular;
        font-size: 2.4rem;
      }

      & > div.teaser {
        @include font-light;
        font-size: 1.8rem;

        p {
          margin: 20px 0 0;
        }
      }

      & > em {
        position: absolute;
        display: block;
        pointer-events: none;
        width: 50px;
        height: 50px;
        border-color: black;
        border-style: solid;
        border-width: 1px;

        &:first-of-type {
          top: -1px;
          left: -1px;
          border-bottom-width: 0;
          border-right-width: 0;
        }
        &:last-of-type {
          bottom: -1px;
          right: -1px;
          border-left-width: 0;
          border-top-width: 0;
        }
      }
    }
  }
}
</style>
